import React, { useEffect, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";

// ─── Costeo por OT (27-ago) ──────────────────────────────────────────────────
// Panel standalone: cuánto costó este trabajo (compras del SII + rendiciones
// vinculadas) contra su venta, con margen $ y %. Solo LEE — la vinculación
// compra→OT se hace en Contabilidad (panel de la factura) y es opcional:
// una OT sin costos vinculados simplemente muestra el estado vacío.
// Props: workOrderId (ObjectId) o number (N° de OT) — el backend acepta ambos.

const fmtM = n => "$" + Math.round(Number(n) || 0).toLocaleString("es-CL");
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const CAT_LABEL = {
  combustible: "Combustible", materiales: "Materiales", ferreteria: "Ferretería",
  transporte: "Transporte", alimentacion: "Alimentación", peajes: "Peajes",
  servicios: "Servicios", otros: "Otros",
};

const colorMargen = pct => {
  if (pct === null || pct === undefined) return "#8a8a8a";
  if (pct < 0)  return "#e74c3c";
  if (pct < 20) return "#f39c12";
  return "#2ecc71";
};

const S = {
  wrap: { fontSize: 12, color: "#ddd" },
  card: {
    background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
    padding: "12px 14px", marginBottom: 10,
  },
  secTitle: {
    fontSize: 10, fontWeight: 700, color: "#cbd5e1",
    textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8,
  },
  kpiLabel: { fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" },
  kpiValue: { fontSize: 16, fontWeight: 700, marginTop: 2, fontVariantNumeric: "tabular-nums" },
  row: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "6px 0", borderBottom: "1px solid #222", fontSize: 11,
  },
  monto: { marginLeft: "auto", fontVariantNumeric: "tabular-nums", fontWeight: 600, whiteSpace: "nowrap" },
};

const CostosOT = ({ workOrderId, number }) => {
  // Pulido desktop 27-ago: compras y rendiciones lado a lado en pantalla ancha.
  const isMobile = useIsMobile();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [oculto, setOculto]   = useState(false); // 403: rol sin acceso a contabilidad

  const key = workOrderId || number;

  useEffect(() => {
    if (!key) return;
    let vivo = true;
    setLoading(true); setError(null);
    backend.get(`sii/costos-ot?workOrder=${encodeURIComponent(key)}`)
      .then(({ data }) => { if (vivo) setData(data); })
      .catch(e => {
        if (!vivo) return;
        // operadores sin rol contable: el panel se oculta sin ruido
        if (e?.response?.status === 403) setOculto(true);
        else setError(e?.response?.data?.error || e.message);
      })
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
  }, [key]);

  if (!key || oculto) return null;
  if (loading) return <div style={{ padding: 12, color: "#aaa", fontSize: 11 }}>Cargando costos…</div>;
  if (error) return (
    <div style={{ padding: 12, color: "#e74c3c", fontSize: 11 }}>
      No se pudieron cargar los costos: {error}
    </div>
  );
  if (!data) return null;

  const { venta, costos, margen, detalle, material } = data;
  const sinCostos = (costos?.nDocs || 0) === 0;
  const cMargen = colorMargen(margen?.pct);

  return (
    <div style={S.wrap}>

      {/* tarjeta margen: venta vs costos */}
      <div style={{ ...S.card, display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <div style={S.kpiLabel}>Venta (total OT)</div>
          <div style={{ ...S.kpiValue, color: "#3498db" }}>{fmtM(venta?.totalOT)}</div>
          <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>
            facturado {fmtM(venta?.facturado)} · {venta?.nFacturas || 0} doc{(venta?.nFacturas || 0) === 1 ? "" : "s"}
          </div>
        </div>
        <div>
          <div style={S.kpiLabel}>Costos vinculados</div>
          <div style={{ ...S.kpiValue, color: "#e67e22" }}>{fmtM(costos?.total)}</div>
          <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>
            compras {fmtM(costos?.compras)} · rendiciones {fmtM(costos?.rendiciones)}
          </div>
        </div>
        <div>
          <div style={S.kpiLabel}>Margen</div>
          <div style={{ ...S.kpiValue, color: cMargen }}>
            {fmtM(margen?.monto)}
            <span style={{ fontSize: 12, marginLeft: 6 }}>
              {margen?.pct === null || margen?.pct === undefined ? "(sin base de venta)" : `(${margen.pct}%)`}
            </span>
          </div>
        </div>
      </div>

      {/* estado vacío: sin compras ni rendiciones vinculadas */}
      {sinCostos ? (
        <div style={{
          ...S.card, textAlign: "center", padding: "18px 14px",
          color: "#8a8a8a", fontStyle: "italic",
        }}>
          Esta OT aún no tiene costos vinculados — vincula compras desde
          Contabilidad → panel de la factura.
        </div>
      ) : (
        <>
          {/* Desktop: las dos listas lado a lado; móvil: apiladas como siempre */}
          <div style={isMobile ? undefined : { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
          {/* compras vinculadas */}
          {(detalle?.compras || []).length > 0 && (
            <div style={{ ...S.card, ...(isMobile ? null : { flex: "1 1 420px", minWidth: 0, marginBottom: 0 }) }}>
              <div style={S.secTitle}>
                Compras vinculadas ({detalle.compras.length})
              </div>
              {detalle.compras.map(f => (
                <div key={f._id} style={S.row}
                  onMouseEnter={e => { if (!isMobile) e.currentTarget.style.background = "#1e1e1e"; }}
                  onMouseLeave={e => { if (!isMobile) e.currentTarget.style.background = ""; }}>
                  <span style={{ fontFamily: "monospace", color: "#3498db" }}>
                    {f.tipoDoc === 61 ? "NC" : "F"} {f.folio}
                  </span>
                  <span style={{ color: "#cbd5e1", whiteSpace: "nowrap" }}>{fmtD(f.fecha)}</span>
                  <span style={{
                    color: "#ddd", overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", minWidth: 0,
                  }}>
                    {f.proveedor}
                  </span>
                  {f.repartida && (
                    <span title="Factura repartida entre varias OTs — se muestra solo la parte de esta OT"
                      style={{ fontSize: 9, color: "#f39c12", border: "1px solid #4a3500", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>
                      parte del reparto
                    </span>
                  )}
                  {f.pagada && (
                    <span style={{ fontSize: 9, color: "#2ecc71", whiteSpace: "nowrap" }}>✓ pagada</span>
                  )}
                  <span style={{ ...S.monto, color: f.netoOT < 0 ? "#2ecc71" : "#e67e22" }}>
                    {fmtM(f.netoOT)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* rendiciones vinculadas */}
          {(detalle?.rendiciones || []).length > 0 && (
            <div style={{ ...S.card, ...(isMobile ? null : { flex: "1 1 420px", minWidth: 0, marginBottom: 0 }) }}>
              <div style={S.secTitle}>
                Rendiciones vinculadas ({detalle.rendiciones.length})
              </div>
              {detalle.rendiciones.map(r => (
                <div key={r._id} style={S.row}
                  onMouseEnter={e => { if (!isMobile) e.currentTarget.style.background = "#1e1e1e"; }}
                  onMouseLeave={e => { if (!isMobile) e.currentTarget.style.background = ""; }}>
                  <span style={{ color: "#cbd5e1", whiteSpace: "nowrap" }}>{fmtD(r.fecha)}</span>
                  <span style={{ color: "#a78bfa", whiteSpace: "nowrap" }}>
                    {CAT_LABEL[r.categoria] || r.categoria}
                  </span>
                  <span style={{
                    color: "#ddd", overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", minWidth: 0,
                  }}>
                    {r.descripcion || r.por || "—"}
                  </span>
                  {r.estado === "pendiente" && (
                    <span style={{ fontSize: 9, color: "#f39c12", whiteSpace: "nowrap" }}>pendiente de aprobar</span>
                  )}
                  <span style={{ ...S.monto, color: "#e67e22" }}>{fmtM(r.neto)}</span>
                </div>
              ))}
            </div>
          )}
          </div>

          <div style={{ fontSize: 10, color: "#8a8a8a" }}>
            Netos (sin IVA), notas de crédito restan. Para vincular más compras:
            Contabilidad → panel de la factura. Vincular es opcional — nada se bloquea sin OT.
          </div>
        </>
      )}
    <MaterialCalculado material={material} />
    </div>
  );
};


/**
 * El material CALCULADO (C21 F1).
 *
 * Va en su propio bloque y con su etiqueta de origen porque NO está sumado al costo de
 * arriba: si ese material se compró con una factura vinculada, ya está contado ahí, y
 * sumarlo lo contaría dos veces. Mostrarlo junto sin decirlo daría un margen en el que
 * nadie podría confiar.
 *
 * Las piezas que no se pudieron valorizar salen igual, con el motivo. Esconderlas daría un
 * costo bajo que parece completo — que es exactamente el error que este bloque viene a
 * evitar.
 *
 * Y por lo mismo se distingue de dónde salió cada costo unitario (ERP-A1): el promedio de
 * las compras recibidas es un número sólido; el precio cargado a mano en Inventario sirve
 * para tener una cifra pero se mueve con la última barra que alguien anotó. Van con
 * etiquetas distintas y el pie dice cuánto del total viene de cada uno.
 */
const MaterialCalculado = ({ material }) => {
  if (!material || !material.lineas?.length) return null;
  const clp = n => `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;
  return (
    <div style={{ background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
                  padding: "10px 14px", marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: ".05em" }}>
          Material calculado
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 8,
                       background: "#a78bfa22", color: "#a78bfa", border: "1px solid #a78bfa55" }}>
          calculado
        </span>
        <span style={{ flex: 1 }} />
        <b style={{ fontSize: 14, color: "#ddd" }}>{clp(material.total)}</b>
      </div>

      <div style={{ fontSize: 10, color: "#f39c12", marginBottom: 8 }}>
        ⚠ No está sumado al costo de arriba: si el material se compró con una factura
        vinculada, ya está contado ahí.
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <tbody>
          {material.lineas.map((l, i) => (
            <tr key={i} style={{ borderTop: "1px solid #222" }}>
              <td style={{ padding: "3px 4px", color: "#ddd" }}>
                {l.sku ? `${l.sku} · ` : ""}{l.nombre}
              </td>
              <td style={{ padding: "3px 4px", color: "#8a8a8a" }}>
                {l.estado === "calculado" ? (
                  <>
                    {l.consumoTotal} {l.unidad} de {l.material} × {clp(l.costoUnitario)}
                    {l.origenCosto === "ultimo_precio" && (
                      <span title={l.origenCostoTexto} style={{ marginLeft: 6, fontSize: 9, fontWeight: 700,
                        padding: "1px 5px", borderRadius: 7, background: "#f39c1222",
                        color: "#f39c12", border: "1px solid #f39c1255" }}>
                        precio a mano
                      </span>
                    )}
                  </>
                ) : l.motivo}
              </td>
              <td style={{ padding: "3px 4px", textAlign: "right",
                           color: l.estado === "calculado" ? "#ddd" : "#8a8a8a",
                           fontVariantNumeric: "tabular-nums" }}>
                {l.estado === "calculado" ? clp(l.monto) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {material.sinCalcular > 0 && (
        <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 6 }}>
          {material.calculadas} de {material.calculadas + material.sinCalcular} piezas
          valorizadas. Las otras necesitan material, rinde o una compra con precio.
        </div>
      )}

      {/* De dónde viene la plata. Sólo se muestra si hay mezcla o si TODO viene del
          precio cargado a mano — que es el caso que hay que mirar con cuidado. */}
      {material.porOrigen?.ultimo_precio > 0 && (
        <div style={{ fontSize: 10, color: "#f39c12", marginTop: 4 }}>
          {material.porOrigen.promedio_movil > 0
            ? <>{clp(material.porOrigen.promedio_movil)} sale del promedio de compras
                y {clp(material.porOrigen.ultimo_precio)} del precio cargado a mano.</>
            : <>Todo este monto usa el precio cargado a mano en Inventario: ninguno de
                estos materiales tiene compras recibidas todavía.</>}
        </div>
      )}
    </div>
  );
};

export default CostosOT;
